import { useState, useMemo } from 'react';
import { getAllWinners, getWinnersByTimeFilter, getLatestWinners, getFeaturedWinners, winnerTypeLabels, winnerTypeIcons, winnerTypeColors, winnerBadgeConfigs, winnerGlobalStats, formatWinnerDate, getRelativeTime, getPositionLabel, type RfwWinner, type WinnerTimeFilter, type WinnerType } from '@/mocks/rfwWinners';

const TIME_FILTERS: { id: WinnerTimeFilter; label: string; icon: string }[] = [
  { id: 'hoy', label: 'Hoy', icon: 'ri-calendar-line' },
  { id: 'semana', label: 'Semana', icon: 'ri-calendar-2-line' },
  { id: 'mes', label: 'Mes', icon: 'ri-calendar-check-line' },
  { id: 'ano', label: 'Año', icon: 'ri-calendar-todo-line' },
  { id: 'historico', label: 'Histórico', icon: 'ri-history-line' },
];

const ALL_TYPES: (WinnerType | 'todos')[] = ['todos', 'concurso', 'trivia', 'encuesta', 'evento', 'club', 'radio-vs-radio', 'programa-vs-programa', 'locutor-vs-locutor'];

export default function AppWinnersTab() {
  const [timeFilter, setTimeFilter] = useState<WinnerTimeFilter>('semana');
  const [typeFilter, setTypeFilter] = useState<WinnerType | 'todos'>('todos');
  const [selectedWinner, setSelectedWinner] = useState<RfwWinner | null>(null);

  const filtered = useMemo(() => {
    let result = getWinnersByTimeFilter(timeFilter);
    if (typeFilter !== 'todos') result = result.filter(w => w.type === typeFilter);
    return result;
  }, [timeFilter, typeFilter]);

  const featured = useMemo(() => getFeaturedWinners(), []);
  const latest = useMemo(() => getLatestWinners(4), []);
  const stats = winnerGlobalStats;

  // Count of active and type filters currently selected
  const typeCounts = useMemo(() => {
    const all = getWinnersByTimeFilter(timeFilter);
    const counts: Record<string, number> = { todos: all.length };
    ALL_TYPES.filter(t => t !== 'todos').forEach(t => { counts[t] = all.filter(w => w.type === t).length; });
    return counts;
  }, [timeFilter]);

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-yellow-100 flex items-center justify-center">
            <i className="ri-trophy-fill text-amber-600 text-lg"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-foreground-950">Ganadores</h2>
            <p className="text-[11px] text-foreground-400">{stats.totalWinners.toLocaleString()} ganadores totales · {stats.winnersThisWeek} esta semana</p>
          </div>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { label: 'Hoy', value: stats.winnersToday, icon: 'ri-calendar-line', color: 'text-red-600 bg-red-50' },
            { label: 'Semana', value: stats.winnersThisWeek, icon: 'ri-calendar-2-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Total Pts', value: `${(stats.totalPointsAwarded / 1000).toFixed(0)}K`, icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
          ].map(s => (
            <div key={s.label} className={`flex items-center gap-2 p-2 rounded-xl ${s.color} bg-opacity-50 border`}>
              <i className={`${s.icon} text-sm`}></i>
              <div>
                <p className="text-sm font-bold text-foreground-900">{s.value}</p>
                <p className="text-[9px] text-foreground-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Time filters */}
        <div className="flex gap-1.5 mb-3">
          {TIME_FILTERS.map(tf => (
            <button key={tf.id} onClick={() => setTimeFilter(tf.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                timeFilter === tf.id ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}>
              <i className={`${tf.icon} text-[11px]`}></i>{tf.label}
            </button>
          ))}
        </div>

        {/* Type chips */}
        <div className="flex gap-1.5 flex-wrap">
          {ALL_TYPES.map(t => {
            const isActive = typeFilter === t;
            const colors = t !== 'todos' ? winnerTypeColors[t as WinnerType] : '';
            return (
              <button key={t} onClick={() => setTypeFilter(t === typeFilter ? 'todos' : t)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  isActive ? `bg-foreground-950 text-background-50` : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}>
                {t !== 'todos' && <i className={`${winnerTypeIcons[t as WinnerType]} text-[11px]`}></i>}
                {t === 'todos' ? `Todos (${typeCounts.todos})` : `${winnerTypeLabels[t as WinnerType]} (${typeCounts[t] || 0})`}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2">
        <p className="text-[11px] text-foreground-400">{filtered.length} ganador{filtered.length !== 1 ? 'es' : ''}</p>
      </div>

      {/* Winners list */}
      <div className="px-3 pb-6 space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-trophy-line text-xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">Sin ganadores</p>
            <p className="text-[11px] text-foreground-300">Intentá otro período de tiempo</p>
          </div>
        ) : filtered.map(winner => {
          const posLabel = getPositionLabel(winner.position);
          const colors = winnerTypeColors[winner.type];
          const relTime = getRelativeTime(winner.date);

          return (
            <div key={winner.id} onClick={() => setSelectedWinner(winner)}
              className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer">
              <div className="flex items-center gap-3 p-3">
                <div className="relative shrink-0">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-background-100">
                    <img src={winner.photoUrl} alt={winner.name} className="w-full h-full object-cover object-top" />
                  </div>
                  {winner.position === 1 && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center border-2 border-background-50">
                      <span className="text-[10px]">🥇</span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[8px] font-bold ${colors}`}>
                      <i className={`${winnerTypeIcons[winner.type]} text-[8px]`}></i>
                      {winnerTypeLabels[winner.type]}
                    </span>
                    {winner.featured && <span className="text-[8px] font-bold text-amber-600">⭐ DESTACADO</span>}
                  </div>
                  <h3 className="text-sm font-bold text-foreground-950 truncate">{winner.name}</h3>
                  <p className="text-[10px] text-foreground-500 truncate">{winner.contestName}</p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-foreground-400">
                    <span className="flex items-center gap-0.5"><i className="ri-map-pin-line text-[9px]"></i>{winner.city}</span>
                    <span className="flex items-center gap-0.5"><i className="ri-radio-line text-[9px]"></i>{winner.radioName}</span>
                    <span className="flex items-center gap-0.5 text-amber-600 font-semibold"><i className="ri-flashlight-line text-[9px]"></i>+{winner.scoreEarned} pts</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-semibold text-foreground-500">{relTime}</p>
                  {winner.position > 1 && <p className={`text-[10px] font-bold ${posLabel.color}`}>{posLabel.emoji}</p>}
                  {winner.badges.length > 0 && (
                    <p className="text-[8px] text-foreground-400 mt-0.5">{winner.badges.length} badge{winner.badges.length > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── WINNER DETAIL MODAL ─── */}
      {selectedWinner && (() => {
        const w = selectedWinner;
        const posLabel = getPositionLabel(w.position);
        const colors = winnerTypeColors[w.type];

        return (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedWinner(null)}></div>
            <div className="relative bg-background-50 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.2s_ease-out]">
              <button onClick={() => setSelectedWinner(null)} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-700"></i>
              </button>

              <div className="flex flex-col items-center pt-8 pb-4 px-4">
                <div className="relative mb-3">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-background-100 border-4 border-amber-200">
                    <img src={w.photoUrl} alt={w.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center border-2 border-background-50">
                    <span className="text-sm">{posLabel.emoji}</span>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[9px] font-bold ${colors} mb-2`}>
                  <i className={`${winnerTypeIcons[w.type]} text-[9px]`}></i>{winnerTypeLabels[w.type]}
                </span>
                <h2 className="text-lg font-extrabold text-foreground-950">{w.name}</h2>
                <p className="text-[11px] text-foreground-500">{w.city}</p>

                <div className="w-full mt-4 space-y-2">
                  <div className="p-3 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Competencia</p>
                    <p className="text-xs font-bold text-foreground-900">{w.contestName}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-xl bg-background-100">
                      <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Radio</p>
                      <p className="text-xs font-bold text-foreground-900">{w.radioName}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-100">
                      <p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Programa</p>
                      <p className="text-xs font-bold text-foreground-900">{w.programName || '—'}</p>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-[10px] text-amber-600 uppercase font-semibold mb-0.5">Premio</p>
                    <p className="text-xs font-bold text-amber-800">{w.prize}</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">{w.prizeDescription}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Fecha</p>
                    <p className="text-xs font-bold text-foreground-900">{formatWinnerDate(w.date)}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <p className="text-[10px] text-foreground-400 uppercase font-semibold">Score</p>
                    <p className="text-sm font-extrabold text-amber-600">+{w.scoreEarned} pts</p>
                  </div>
                </div>

                {/* Badges */}
                {w.badges.length > 0 && (
                  <div className="w-full mt-3">
                    <p className="text-[10px] font-semibold text-foreground-500 mb-2">Insignias</p>
                    <div className="flex flex-wrap gap-1.5">
                      {w.badges.map(badgeId => {
                        const badge = winnerBadgeConfigs.find(b => b.id === badgeId);
                        if (!badge) return null;
                        return (
                          <span key={badgeId} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${badge.color} ${badge.bg} border ${badge.border}`}>
                            {badge.emoji} {badge.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}